import { Dropdown, Flex, Icon, Label, Button } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import {
  Alert,
  Person,
  Status,
  TextCopy,
  Calendar,
  Counter,
  Timeline,
  Tags,
  Mirror,
  Favorite,
  Globe,
  Checkbox as CheckboxIcon,
  Link as LinkIcon,
  Numbers as NumbersIcon,
  Country as CountryIcon,
  Location as LocationIcon,
  Gantt,
  LongText as LongTextIcon,
  Mobile,
  Bolt,
  Formula,
  HighlightColorBucket,
  Recurring,
  Versioning,
  File as FileIcon,
  Email,
  Time as TimeIcon,
  Dropdown as DropdownIcon,
  Mirror as MirrorIcon,
  Lines,
  Group,
  Board,
} from '@vibe/icons';

import { BoardField } from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/types';

const BOARD_COLUMNS_META = [
  {
    column: 'people',
    icon: Person,
    label: 'People',
  },
  {
    column: 'status',
    icon: Status,
    label: 'Status',
  },
  {
    column: 'text',
    icon: TextCopy,
    label: 'Text',
  },
  {
    column: 'date',
    icon: Calendar,
    label: 'Date',
  },
  {
    column: 'numbers',
    icon: Counter,
    label: 'Numbers',
  },
  {
    column: 'timeline',
    icon: Timeline,
    label: 'Timeline',
  },
  {
    column: 'timeline-numeric',
    icon: Timeline,
    label: 'Timeline + Numeric',
  },
  {
    column: 'tags',
    icon: Tags,
    label: 'Tags',
  },
  {
    column: 'connect-boards',
    icon: Mirror,
    label: 'Connect Boards',
  },
  {
    column: 'mirror',
    icon: Mirror,
    label: 'Mirror',
  },
  {
    column: 'rating',
    icon: Favorite,
    label: 'Rating',
  },
  {
    column: 'dropdown',
    icon: DropdownIcon,
    label: 'Dropdown',
  },
  {
    column: 'world-clock',
    icon: Globe,
    label: 'World Clock',
  },
  {
    column: 'checkbox',
    icon: CheckboxIcon,
    label: 'Checkbox',
  },
  {
    column: 'link',
    icon: LinkIcon,
    label: 'Link',
  },
  {
    column: 'auto-number',
    icon: NumbersIcon,
    label: 'Auto Number',
  },
  {
    column: 'country',
    icon: CountryIcon,
    label: 'Country',
  },
  {
    column: 'location',
    icon: LocationIcon,
    label: 'Location',
  },
  {
    column: 'week',
    icon: Calendar,
    label: 'Week',
  },
  {
    column: 'progress-tracking',
    icon: Gantt,
    label: 'Progress Tracking',
  },
  {
    column: 'long-text',
    icon: LongTextIcon,
    label: 'Long Text',
  },
  {
    column: 'phone',
    icon: Mobile,
    label: 'Phone',
  },
  {
    column: 'item-id',
    icon: Bolt,
    label: 'Item ID',
  },
  {
    column: 'formula',
    icon: Formula,
    label: 'Formula',
  },
  {
    column: 'color-picker',
    icon: HighlightColorBucket,
    label: 'Color Picker',
  },
  {
    column: 'last-updated',
    icon: Recurring,
    label: 'Last Updated',
  },
  {
    column: 'time-tracking',
    icon: Versioning,
    label: 'Time Tracking',
  },
  {
    column: 'file',
    icon: FileIcon,
    label: 'File',
  },
  {
    column: 'email',
    icon: Email,
    label: 'Email',
  },
  {
    column: 'hour',
    icon: TimeIcon,
    label: 'Hour',
  },
  {
    column: 'board_relation',
    icon: MirrorIcon,
    label: 'Mirror',
  },
];

type FieldPickerProps = {
  mirrorColumnsDisabled?: boolean;
  fields: BoardField[];
  value?: string;
  onChange: (field: BoardField) => void;
};

export function FieldPicker({ mirrorColumnsDisabled, fields, value, onChange }: FieldPickerProps) {
  const options = fields.map((field) => {
    const meta = BOARD_COLUMNS_META.find((m) => m.column === field.board_field_type);
    const leftIcon = meta?.icon || (field.label === 'Board Name' && Board) || (field.label === 'Group Name' && Group) || Lines;
    const label = meta?.label || (field.label === 'Board Name' && 'Board') || (field.label === 'Group Name' && 'Group') || '';

    return {
      value: field.id,
      // Vibe 4's default filter calls label.toLowerCase(), which throws on the JSX label below. filterOption uses this.
      searchText: `${field.label} ${label}`,
      label: (
        <Flex gap={8} align="center">
          <Icon icon={leftIcon} size={16} />
          {field.label}
          {label !== '' && <Label text={label} kind="line" size="small" color="dark" />}
          {field.board_field_type === 'formula' ? <Label text="Experimental" kind="fill" size="small" color="dark" /> : null}
          {mirrorColumnsDisabled && label === 'Mirror' ? <Label text="Disabled" kind="fill" size="small" color="negative" /> : null}
        </Flex>
      ),
    };
  });

  let selectedOption = options.find((option) => option.value === value);

  if (!selectedOption) {
    let columnId;

    try {
      const pieces = value?.split(':');

      if (Array.isArray(pieces)) {
        columnId = pieces[pieces.length - 1];
      }
    } catch (err) {}

    const inaccessibleFieldOption = {
      label: (
        <Flex gap={8} align="center">
          <Icon icon={Alert} size={16} />
          Column not accessible
          <Label text={`ID: ${columnId}`} kind="fill" size="small" color="dark" />
          <Label text="Warning" kind="fill" size="small" color="negative" />
        </Flex>
      ),
      value: '___inaccessible_field___',
      searchText: `Column not accessible ${columnId}`,
    };

    options.unshift(inaccessibleFieldOption);

    selectedOption = inaccessibleFieldOption;
  }

  return (
    <>
      <div
        style={{
          position: 'relative', // z-index fix for dropdowns
          width: '100%',
        }}
      >
        <Flex gap={8} style={{ width: '100%' }}>
          <Dropdown
            searchable
            onOptionSelect={(ev: any) => {
              const fieldId = ev.value;
              const field = fields.find(({ id }) => id === fieldId);

              if (field) {
                onChange(field);
              }
            }}
            value={dropdownValue(selectedOption)}
            options={options}
            filterOption={(option, inputValue) => !inputValue || option.searchText.toLowerCase().includes(inputValue.toLowerCase())}
            placeholder="Select field"
            clearable={false}
            className="board-column-name"
            menuWrapperClassName="dropdown_content_indexed"
          />
          {selectedOption && selectedOption.value === '___inaccessible_field___' ? (
            <div style={{ backgroundColor: '#fff', borderRadius: 4 }}>
              <Button
                size="medium"
                kind="secondary"
                onClick={() => {
                  window.open('https://getgorilla.app/products/spreadsheet-gorilla/incidents', '_blank');
                }}
              >
                Learn more
              </Button>
            </div>
          ) : null}
        </Flex>
      </div>
    </>
  );
}
